/**
 * Retiro de la PWA, lado PAGINA. Complementa al kill-switch de `public/sw.js` (lado service worker).
 *
 * Por que hacen falta los dos: el navegador solo vuelve a pedir `sw.js` al navegar dentro del scope
 * (como maximo una vez cada 24 h) o ante un evento push; un usuario con la PWA instalada podria
 * seguir con el worker viejo hasta un dia. Esta funcion, que corre apenas se abre la portada, da de
 * baja los registros y borra las caches sin esperar esa revision. Si el kill-switch ya lo hizo (o no
 * habia nada), es un no-op.
 *
 * ⚠️ NUNCA lanza: la portada es estatica y no puede depender de que la limpieza funcione. Cada paso
 * se aisla, porque un fallo en uno (p. ej. `caches` ausente en un navegador viejo) no debe impedir el
 * otro. Recibe el entorno por parametro para probarla sin navegador.
 */

export interface RegistroServiceWorker {
  unregister(): Promise<boolean>;
}

export interface EntornoRetiro {
  serviceWorker?: { getRegistrations(): Promise<readonly RegistroServiceWorker[]> } | undefined;
  caches?: { keys(): Promise<string[]>; delete(clave: string): Promise<boolean> } | undefined;
}

export interface ResultadoRetiro {
  registrosDadosDeBaja: number;
  cachesBorradas: number;
}

export async function retirarPwa(entorno: EntornoRetiro): Promise<ResultadoRetiro> {
  const resultado: ResultadoRetiro = { registrosDadosDeBaja: 0, cachesBorradas: 0 };

  try {
    const registros = (await entorno.serviceWorker?.getRegistrations()) ?? [];
    for (const registro of registros) {
      try {
        if (await registro.unregister()) resultado.registrosDadosDeBaja += 1;
      } catch {
        // Un registro que no se deja dar de baja no debe frenar a los demas.
      }
    }
  } catch {
    // Sin service workers (contexto inseguro, navegador viejo) o la API fallo: nada que retirar.
  }

  try {
    const claves = (await entorno.caches?.keys()) ?? [];
    for (const clave of claves) {
      try {
        if (await entorno.caches?.delete(clave)) resultado.cachesBorradas += 1;
      } catch {
        // Idem: seguir con la siguiente cache.
      }
    }
  } catch {
    // Sin Cache API: nada que borrar.
  }

  return resultado;
}
